import 'swiper/css';
import 'swiper/css/pagination';

import { Chip } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useId } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import Icon, { IconName } from '@/components/Icon/Icon';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import Link from '@/components/Link/Link';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import StarRating, {
  type StarRatingValidFilledValues,
} from '@/components/StarRatingComponent/StarRatingComponent';
import {
  type Contentful,
  ContentStyle,
  type LinkContent,
} from '@/contentful/contentful-exports';
import useGlobalStyles from '@/styles/global.styles';

import useStyles from './HighlightTestimonial.styles';

export interface HighlightTestimonialCardContent extends Contentful {
  fields: {
    testimonial: string;
    quoteAttribution: string;
    attributionLink?: string;
    productChips?: string[];
    rating: boolean;
    starRating: StarRatingValidFilledValues;
  };
}

export interface HighlightTestimonialContent extends Contentful {
  fields: {
    style?: ContentStyle;
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    cta?: LinkContent;
    testimonialCards: HighlightTestimonialCardContent[];
  };
}

interface HighlightTestimonialProps {
  content: HighlightTestimonialContent;
}

export default function HighlightTestimonial({
  content: {
    fields: { style, eyebrow, heading, subheading, cta, testimonialCards },
  },
}: HighlightTestimonialProps) {
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const isOnDark = style === ContentStyle.OnDark;
  const { classes, cx } = useStyles({ isOnDark });
  const prevElId = useId().replaceAll(':', '');
  const nextElId = useId().replaceAll(':', '');

  return (
    <div className={cx(classes.container, style)}>
      <GridContainer fixed>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <SectionHeader
            eyebrow={eyebrow}
            heading={heading}
            subheading={subheading}
            isOnDark={isOnDark}
            fixed
          />
        </Grid>
        {cta && (
          <Grid
            className={classes.cta}
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Button
              isOnDark={isOnDark}
              size={{ xs: ButtonSize.SM, sm: ButtonSize.MD }}
              label={cta.fields.text}
              href={cta.fields.url}
              variant={ButtonVariant.SECONDARY}
              className={classes.button}
            />
          </Grid>
        )}
      </GridContainer>
      <div className={classes.testimonialsContainer}>
        <Swiper
          navigation={{
            prevEl: `#${prevElId}`,
            nextEl: `#${nextElId}`,
          }}
          modules={[Navigation, Pagination]}
          slidesPerView={1}
          rewind
          pagination={{
            el: '#bullet-pagination',
            bulletActiveClass: 'bullet-active',
            bulletClass: 'bullet',
            horizontalClass: classes.horizontal,
            clickable: true,
            // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
            renderBullet: (index: number, className: string) => {
              return `<svg class="${className}" width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="4" r="4" fill="currentColor"/>
              </svg>`;
            },
          }}
        >
          {testimonialCards.map(
            (
              {
                fields: {
                  testimonial,
                  quoteAttribution,
                  attributionLink,
                  rating,
                  starRating,
                  productChips,
                },
              },
              index
            ) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
              <SwiperSlide key={index} className={classes.swiperSlide}>
                <GridContainer
                  fixed
                  gridProps={{ className: classes.testimonialGridContainer }}
                >
                  <Grid size={12}>
                    <div className={classes.testimonial}>
                      <div className={classes.starQuoteContainer}>
                        <p
                          className={cx(
                            globalClasses.body,
                            classes.quoteAttribution
                          )}
                        >
                          {attributionLink ? (
                            <Link href={attributionLink}>
                              {quoteAttribution}
                            </Link>
                          ) : (
                            quoteAttribution
                          )}
                        </p>
                        {rating && (
                          <StarRating
                            value={starRating}
                            isOnDark={isOnDark}
                            starHeight={34}
                          />
                        )}
                      </div>

                      <h3
                        className={classes.testimonialText}
                      >{`"${testimonial}"`}</h3>
                      {productChips && (
                        <div className={classes.chips}>
                          {productChips?.map((productChip, index) => (
                            <Chip
                              // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                              key={index}
                              label={productChip}
                              className={classes.chip}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </Grid>
                </GridContainer>
              </SwiperSlide>
            )
          )}
        </Swiper>
        <div
          className={cx(
            classes.navigation,
            testimonialCards.length < 2 && classes.hidden
          )}
        >
          <button
            id={prevElId}
            type='button'
            aria-label='Previous testimonial'
            className={classes.navigationButton}
          >
            <Icon name={IconName.ARROW_LEFT} width={24} />
          </button>
          {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
          <div id='bullet-pagination' className={classes.pagination}></div>
          <button
            id={nextElId}
            type='button'
            aria-label='Next testimonial'
            className={classes.navigationButton}
          >
            <Icon name={IconName.ARROW_RIGHT} width={24} />
          </button>
        </div>
      </div>
    </div>
  );
}
